import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { NetworkStatus } from '@apollo/client';
import { TranslateService } from '@ngx-translate/core';
import { QueryRef } from 'apollo-angular';
import { forkJoin, Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, retryWhen, switchMap, takeWhile, tap } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import { environment } from '@env/environment';
import {
  ActionEnum,
  ContextEnum,
  CreditType,
  CreditTypeEnum,
  CustomSignerInput,
  Document,
  DocumentInput,
  DocumentsListColumns,
  DocumentStatusEnum,
  ExcludePlans,
  FieldsValuesBySigner,
  FallbackBehaviorEnum,
  ListFormat,
  ListSorting,
  LockSignerInput,
  OrderByEnum,
  SecurityVerificationEnum,
  Signer,
  SignerMainAttributes,
  User,
  ZipCodeInfo,
  LanguageEnum,
  FieldDataToShow,
  LockSignature,
  LanguageOptions
} from '@app/models';
import { CredentialsService, StorageService, throwOnGraphqlError, I18nService, HelperService } from '@app/core';
import { LoaderService } from '@app/shared/loader/loader.service';
import { CurrentCreditsPriceForDocumentGQL, DynamicTemplateVariable, SecurityVerificationInput } from 'src/generated/graphql.default';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export enum AppStorageKeys {
  Darkmode = 'darkmode',
  ShowSandbox = 'showSandbox',
  ListFormat = 'listFormat',
  ListSorting = 'listSortingV2',
  ListDirection = 'listDirectionV2',
  DocumentStatusEnum = 'documentStatusFilterV2',
  DocumentsListColumns = 'documentsListColumns',
  SearchParams = 'searchParams',
  FirstPageCounts = 'firstPageCounts',
  FolderContext = 'folderContext',
  UrlChangeInfo = 'urlChangeInfoV2',
  // remover depois
  LockedSigners = 'lockedSignersV2',
  LockedCC = 'lockedCC',
  LockedReplyTo = 'lockedReplyTo',
  DocumentParams = 'documentParams',
  DocumentSections = 'documentSections'
}

@Injectable({
  providedIn: 'root'
})
export class AppService extends HelperService {
  private _isDarkmode: boolean;
  private _showSandbox: boolean;
  private _listFormat: ListFormat;
  private _listSorting: ListSorting;
  private _listDirection: OrderByEnum;
  private _documentStatusFilter: DocumentStatusEnum;
  private _documentsListColumns: DocumentsListColumns[];
  private _searchParams: { [k: string]: any };
  private _creditsPriceTable: CreditType[] = [];
  private _firstPageCounts: { [k: string]: any };
  private _folderContext: ContextEnum;
  private _urlChangeInfo: string[];
  private _isEnterpriseChild = false;
  private readonly baseUrl = 'https://autentique.com.br';

  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService,
    private credentialsService: CredentialsService,
    private ngZone: NgZone,
    private loaderService: LoaderService,
    private currentCreditsPriceForDocumentGQL: CurrentCreditsPriceForDocumentGQL,
    private translateService: TranslateService,
    private i18nService: I18nService
  ) {
    super();

    const defaultDocumentsListColumns = [DocumentsListColumns.Name, DocumentsListColumns.Status, DocumentsListColumns.Signers, DocumentsListColumns.CreatedAt];

    // window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    this._isDarkmode = !!parseInt(this.storageService.get(AppStorageKeys.Darkmode));
    this._showSandbox = !!parseInt(this.storageService.get(AppStorageKeys.ShowSandbox));
    this._listFormat = (this.storageService.get(AppStorageKeys.ListFormat) as ListFormat) || ListFormat.Box;

    // Migrate from deprecated ListSorting.Relevance to ListSorting.CreatedAt
    const storedListSortingValue = this.storageService.get(AppStorageKeys.ListSorting);
    let storedListSorting: ListSorting;

    if (storedListSortingValue === 'Relevance') {
      storedListSorting = ListSorting.CreatedAt;
      this.storageService.set(AppStorageKeys.ListSorting, ListSorting.CreatedAt);
    } else {
      storedListSorting = (storedListSortingValue as ListSorting) || ListSorting.CreatedAt;
    }
    //

    this._listSorting = storedListSorting;

    this._listDirection = (this.storageService.get(AppStorageKeys.ListDirection) as OrderByEnum) || OrderByEnum.Desc;
    this._documentStatusFilter = (this.storageService.get(AppStorageKeys.DocumentStatusEnum) as DocumentStatusEnum) || null;
    this._documentsListColumns = (this.storageService.get(AppStorageKeys.DocumentsListColumns) || defaultDocumentsListColumns.join(',')).split(',').filter(value => !!value) as DocumentsListColumns[];

    this._folderContext = (this.storageService.get(AppStorageKeys.FolderContext) as ContextEnum) || ContextEnum.User;
    this._searchParams = this.storageService.getJSON(AppStorageKeys.SearchParams) || {};
    this._firstPageCounts = this.storageService.getJSON(AppStorageKeys.FirstPageCounts) || {};
    this._urlChangeInfo = this.storageService.getJSON(AppStorageKeys.UrlChangeInfo) || [];
  }

  get isDarkmode() {
    return this._isDarkmode;
  }
  set isDarkmode(value: boolean) {
    this._isDarkmode = value;
    this.storageService.set(AppStorageKeys.Darkmode, value ? 1 : 0);
  }

  get showSandbox() {
    return this._showSandbox;
  }
  set showSandbox(value: boolean) {
    this._showSandbox = value;
    this.storageService.set(AppStorageKeys.ShowSandbox, value ? 1 : 0);
  }

  get listFormat() {
    return this._listFormat;
  }
  set listFormat(value: ListFormat) {
    this._listFormat = value;
    this.storageService.set(AppStorageKeys.ListFormat, value);
  }

  get listSorting() {
    return this._listSorting;
  }
  set listSorting(value: ListSorting) {
    this._listSorting = value;
    this.storageService.set(AppStorageKeys.ListSorting, value);
  }

  get listDirection() {
    return this._listDirection;
  }
  set listDirection(value: OrderByEnum) {
    this._listDirection = value;
    this.storageService.set(AppStorageKeys.ListDirection, value);
  }

  get documentStatusFilter() {
    return this._documentStatusFilter;
  }
  set documentStatusFilter(value: DocumentStatusEnum) {
    this._documentStatusFilter = value;
    this.storageService.set(AppStorageKeys.DocumentStatusEnum, value || '');
  }

  get documentsListColumns() {
    return this._documentsListColumns;
  }
  set documentsListColumns(values: DocumentsListColumns[]) {
    this._documentsListColumns = (values || []).filter(value => !!value);
    this.storageService.set(AppStorageKeys.DocumentsListColumns, this._documentsListColumns.join(',') || '');
  }

  get searchParams() {
    return this._searchParams;
  }
  set searchParams(params: { [k: string]: any }) {
    this._searchParams = cloneDeep(params);
    this.storageService.setJSON(AppStorageKeys.SearchParams, {
      includeArchived: params.includeArchived || undefined,
      includeDeleted: params.includeDeleted || undefined
    });
  }

  get folderContext() {
    return this._folderContext;
  }
  set folderContext(context: ContextEnum) {
    this._folderContext = context;
    this.storageService.set(AppStorageKeys.FolderContext, context);
  }

  get previousUrl() {
    return this._urlChangeInfo[1] && this._urlChangeInfo[1] === location.pathname ? this._urlChangeInfo[0] || null : null;
  }

  get creditsPriceTable() {
    return this._creditsPriceTable;
  }

  get isEnterpriseChild() {
    return this._isEnterpriseChild;
  }

  set isEnterpriseChild(value: boolean) {
    this._isEnterpriseChild = value;
  }

  creditPriceByType(type: string) {
    return type ? this.creditsPriceTable.find(credit => credit.value === type)?.cost : 0;
  }

  totalCreditPrice(signers: CustomSignerInput[], isMarkdown: boolean = false) {
    return this.creditsPriceTable.length <= 0
      ? 0
      : (signers || []).reduce((total, signer) => {
          const isMarkdownWhatsappSigner = isMarkdown && signer !== null && signer._extra.signerMainAttribute === SignerMainAttributes.Whatsapp;

          const creditsTypeMap = {
            [CreditTypeEnum.SecurityVerificationSms]: signer?._extra.showSmsVerification && [SignerMainAttributes.Email, SignerMainAttributes.Name].includes(signer?._extra.signerMainAttribute),
            [CreditTypeEnum.DeliveryMethodSms]: [SignerMainAttributes.Sms].includes(signer?._extra.signerMainAttribute),
            [CreditTypeEnum.DeliveryMethodWhatsapp]: [SignerMainAttributes.Whatsapp].includes(signer?._extra.signerMainAttribute),
            [CreditTypeEnum.SecurityVerificationManual]: signer?._extra.showSecurityVerifications && signer?._extra.documentType === SecurityVerificationEnum.Manual && !isMarkdownWhatsappSigner,
            [CreditTypeEnum.SecurityVerificationUpload]: signer?._extra.showSecurityVerifications && signer?._extra.documentType === SecurityVerificationEnum.Upload && !isMarkdownWhatsappSigner,
            [CreditTypeEnum.SecurityVerificationLive]: signer?._extra.showSecurityVerifications && signer?._extra.documentType === SecurityVerificationEnum.Live && !isMarkdownWhatsappSigner,
            [CreditTypeEnum.SecurityVerificationPfFacial]: signer?._extra.showSecurityVerifications && signer?._extra.documentType === SecurityVerificationEnum.PfFacial && !isMarkdownWhatsappSigner,
            [CreditTypeEnum.SecurityVerificationBiometricAndTextExtraction]:
              signer?._extra.showSecurityVerifications && signer?._extra.documentType === SecurityVerificationEnum.BiometricAndTextExtraction && !isMarkdownWhatsappSigner,
            [CreditTypeEnum.SecurityVerificationLivenessAndTextExtraction]:
              signer?._extra.showSecurityVerifications && signer?._extra.documentType === SecurityVerificationEnum.LivenessAndTextExtraction && !isMarkdownWhatsappSigner,
            [CreditTypeEnum.SecurityVerificationPfFacialMatch]:
              signer?._extra.showSecurityVerifications && signer?._extra.documentType === SecurityVerificationEnum.PfFacialMatch && !isMarkdownWhatsappSigner
          };
          return (
            total +
            Object.keys(creditsTypeMap)
              .filter((_, i) => Object.values(creditsTypeMap)[i])
              .reduce((sum, type) => sum + (this.creditsPriceTable.find(credit => credit.value === type)?.cost || 0), 0)
          );
        }, 0);
  }

  getFirstPageCount(key: string) {
    return this._firstPageCounts[key];
  }

  addFirstPageCounts(params: { [k: string]: any }) {
    this._firstPageCounts = cloneDeep({ ...this._firstPageCounts, ...(params || {}) });
    this.storageService.setJSON(AppStorageKeys.FirstPageCounts, this._firstPageCounts);
  }

  getZipCodeInfo(cep: string) {
    return this.httpClient
      .disableLoader()
      .get<ZipCodeInfo>('https://viacep.com.br/ws/' + cep + '/json/')
      .pipe(switchMap(data => (data.localidade ? of(data) : throwError('invalid zipcode'))));
  }

  downloadLink(url: string, fileName: string) {
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.style.display = 'none';
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.click();
    setTimeout(() => link.remove());
  }

  downloadData(data: any, fileName: string, type: string = 'octet/stream') {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    this.downloadLink(url, fileName);
    URL.revokeObjectURL(url);
  }

  loadStatusPageWidget() {
    const scriptId = 'statuspage-script';
    if (environment.production && this.isAutentiqueDomain() && !document.getElementById(scriptId)) {
      const scriptElement = document.createElement('script');
      scriptElement.src = 'https://35j8l08fg1jh.statuspage.io/embed/script.js';
      scriptElement.type = 'text/javascript';
      scriptElement.async = true;
      scriptElement.id = scriptId;
      scriptElement.onload = () => {};
      document.getElementsByTagName('head')[0].appendChild(scriptElement);
    }
  }

  formDataToJSON(formData: { [k: string]: any }, typeMap?: { [k: string]: 'string' | 'number' | 'boolean' }) {
    formData = formData || {};
    typeMap = typeMap || {};
    const jsonEntries = Object.entries(formData).map(([k, v]) => {
      let value;
      if (typeMap[k] === 'string') {
        value = String(v);
      } else if (typeMap[k] === 'number') {
        value = String(v).match('.') ? parseFloat(String(v)) : parseInt(String(v));
      } else if (typeMap[k] === 'boolean') {
        value = v !== false && v !== 0 && String(v).toLowerCase() !== 'false' && v !== '' && v !== '0' && String(v).toLowerCase() !== 'null' && String(v).toLowerCase() !== 'undefined';
      } else {
        value = v;
      }
      return [k, value];
    });

    function parseJsonFromForm(flatJsonArray: any) {
      let json = {};
      flatJsonArray.forEach((elementInArray: any) => {
        json = parseNestedJson(elementInArray[0], elementInArray[1], json);
      });
      return json;
    }
    function parseNestedJson(childNameString: any, value: any, parentJson: any) {
      if (childNameString.indexOf('.') === -1) {
        parentJson[childNameString] = value;
      } else {
        const nestedNames = childNameString.split(/\.(.*)/, 2);
        if (!parentJson[nestedNames[0]]) {
          if (isNaN(nestedNames[0])) {
            parentJson[nestedNames[0]] = {};
          } else {
            if (!Array.isArray(parentJson)) {
              parentJson = [];
            }
          }
        }
        if (parentJson[nestedNames[0]]) {
          parentJson[nestedNames[0]] = parseNestedJson(nestedNames[1], value, parentJson[nestedNames[0]]);
        } else {
          parentJson.push(parseNestedJson(nestedNames[1], value, {}));
        }
      }
      return parentJson;
    }
    return parseJsonFromForm(jsonEntries);
  }

  refreshCreditsPrice(): Observable<CreditType[]> {
    let base$ = this.credentialsService.isAuthenticated() ? this.currentCreditsPriceForDocumentGQL.fetch(null, { fetchPolicy: 'cache-first' }) : of(null);

    let request$ = base$.pipe(
      filter(response => !!response?.data),
      map(response => response.data.howMuchDoesItCost),
      tap(priceTable => (this._creditsPriceTable = priceTable))
    );

    request$.subscribe();
    return request$;
  }

  waitUntilUrlsExist(urls: string[]) {
    const clonedUrls = cloneDeep(urls || []);
    return of(null).pipe(
      switchMap(() =>
        forkJoin(
          clonedUrls.map(url =>
            this.httpClient
              .disableLoader()
              .disableVersionUpdate()
              .disableApiPrefix()
              .get(url, { responseType: 'blob' })
              .pipe(
                tap(() =>
                  clonedUrls.splice(
                    clonedUrls.findIndex(thisUrl => thisUrl === url),
                    1
                  )
                ),
                catchError(() => of(null))
              )
          )
        )
      ),
      switchMap(() => (clonedUrls.length > 0 ? throwError(null) : of(null))),
      retryWhen(
        mergeMap((error, i) => {
          const retryAttempt = i + 1;
          if (retryAttempt > 50) {
            return throwError(error);
          }
          return timer(retryAttempt * 1000);
        })
      )
    );
  }

  downsizeImage(image: File, maxSizePx: number) {
    return new Observable<File>(subscriber => {
      const img = new Image();
      const imageSrc = URL.createObjectURL(image);
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        const widthRatio = maxSizePx / width;
        const heightRatio = maxSizePx / height;
        const scale = Math.min(widthRatio, heightRatio);
        if (scale < 1) {
          width *= scale;
          height *= scale;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          blob => {
            URL.revokeObjectURL(imageSrc);
            this.ngZone.run(() => {
              subscriber.next(new File([blob], image.name, { type: blob.type, lastModified: Date.now() }));
              subscriber.complete();
            });
          },
          'image/jpeg',
          0.9
        );
      };
      img.onerror = err => {
        URL.revokeObjectURL(imageSrc);
        this.ngZone.run(() => subscriber.error(err));
      };

      img.src = imageSrc;
    });
  }

  setNewUrl(url: string) {
    this._urlChangeInfo = [this._urlChangeInfo[1] !== url ? this._urlChangeInfo[1] || null : this._urlChangeInfo[0] || null, url];
    this.storageService.setJSON(AppStorageKeys.UrlChangeInfo, this._urlChangeInfo, true);
  }

  watchCacheAndNetworkBase<T>(queryRef: QueryRef<T, any>) {
    const isCacheAndNetwork = queryRef.options.fetchPolicy === 'cache-and-network';

    return of(null).pipe(
      tap(() => (this.loaderService.disableHttpLoader = isCacheAndNetwork)),
      switchMap(() => queryRef.valueChanges),
      throwOnGraphqlError(),
      takeWhile(response => response?.networkStatus !== NetworkStatus.ready, true),
      filter(response => !!response?.data),
      finalize(() => (this.loaderService.disableHttpLoader = isCacheAndNetwork ? false : this.loaderService.disableHttpLoader))
    );
  }

  isHexaColor(color: string) {
    return color && (color.length === 7 || /^#[0-9a-fA-F]{6}$/.test(color));
  }

  getContrastColor(color: string = '#0461ff') {
    if (!this.isHexaColor(color)) {
      return '#000000';
    } else {
      const [, red, green, blue] = /#?(\w{2})(\w{2})(\w{2})/.exec(color);
      const brightness = (parseInt(red, 16) * 299 + parseInt(green, 16) * 587 + parseInt(blue, 16) * 114) / 1000;
      return brightness < 128 ? '#ffffff' : '#000000';
    }
  }

  getDateRangePickerOptions() {
    const formatDate = this.i18nService.dateFormatAsMask(null, true) || 'DD/MM/YYYY';
    return {
      ranges: {
        [this.translateService.instant('calendar.today')]: [dayjs(), dayjs()],
        [this.translateService.instant('calendar.yesterday')]: [dayjs().subtract(1, 'days'), dayjs().subtract(1, 'days')],
        [this.translateService.instant('calendar.last7Days')]: [dayjs().subtract(6, 'days'), dayjs()],
        [this.translateService.instant('calendar.last30Days')]: [dayjs().subtract(29, 'days'), dayjs()],
        [this.translateService.instant('calendar.thisMonthToDate')]: [dayjs().startOf('month'), dayjs()],
        [this.translateService.instant('calendar.thisYear')]: [dayjs().startOf('year'), dayjs().endOf('year')],
        [this.translateService.instant('calendar.lastYear')]: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')],
        [this.translateService.instant('calendar.allTime')]: [dayjs('2016-06-12'), dayjs()]
      },
      locale: {
        daysOfWeek: this.translateService.instant('calendar.daysOfWeek').split(','),
        monthNames: this.translateService.instant('calendar.monthNames').split(','),
        format: formatDate,
        displayFormat: formatDate,
        separator: ' - ',
        cancelLabel: this.translateService.instant('calendar.cancel'),
        applyLabel: this.translateService.instant('calendar.apply'),
        clearLabel: this.translateService.instant('calendar.clear'),
        customRangeLabel: this.translateService.instant('calendar.customRange')
      },
      maxDate: dayjs(),
      opens: 'center'
    };
  }

  // TODO: Remover após a data da promocao/blackFriday/carnaval
  isEventSale() {
    const now = new Date();
    const start = new Date('2026-02-09T00:01:00-03:00');
    const end = new Date('2026-02-27T23:59:00-03:00');
    return now >= start && now <= end;
  }
  isEventSaleBannerVisible() {
    return !window.localStorage.getItem('_hideEventSaleCarnaval2026');
  }
  hideEventSaleBanner() {
    window.localStorage.setItem('_hideEventSaleCarnaval2026', '1');
  }
  isUserEligibleForEventSale(currentUser: User) {
    return currentUser && !currentUser?.organization?.parent && !ExcludePlans.includes(currentUser.subscription.plan_id) && this.isAutentiqueDomain() && !this.isEnterpriseChild;
  }

  parseLockSigners(signers: CustomSignerInput[], isQualified = false): LockSignerInput[] {
    return signers
      .map(signer => {
        if (!signer?._extra || !signer?._extra.isLocked) return;

        const securityVerifications: SecurityVerificationInput[] = [];
        if (signer._extra.showSmsVerification && ['email', 'name'].includes(signer._extra.signerMainAttribute) && signer._extra.phoneNumber) {
          securityVerifications.push({ type: SecurityVerificationEnum.Sms, verify_phone: signer._extra.phoneNumber || null });
        }
        if (signer._extra.showSecurityVerifications && signer._extra.documentType) {
          securityVerifications.push({
            type: signer._extra.documentType as SecurityVerificationEnum,
            fallback_behavior: signer._extra.fallbackEnabled ? null : FallbackBehaviorEnum.DisableFallback
          });
        }

        return {
          action: signer.action || ActionEnum.Sign,
          ...(signer._extra.signerMainAttribute ? { delivery_method: signer._extra.signerMainAttribute } : {}),
          ...(signer.email ? { email: signer.email } : {}),
          ...(signer.name ? { name: signer.name } : {}),
          ...(signer.phone || signer.whatsapp || signer.sms ? { phone: signer.phone || signer.whatsapp || signer.sms } : {}),
          ...(securityVerifications?.length > 0 ? { security_verifications: securityVerifications } : {}),
          ...(signer.configs?.cnpj ? { configs: { cnpj: signer.configs.cnpj } } : {}),
          ...(signer.configs?.cpf ? { configs: { cpf: signer.configs.cpf } } : {}),
          ...(signer._extra.showCpfVerification ? { show_cpf_verification: signer._extra.showCpfVerification } : {}),
          ...(signer._extra.showOptions ? { show_options: signer._extra.showOptions } : {}),
          ...(signer._extra.showSecurityVerifications ? { show_security_verifications: signer._extra.showSecurityVerifications } : {}),
          ...(signer._extra.showSmsVerification ? { show_sms_verification: signer._extra.showSmsVerification } : {}),
          ...(signer._extra.isQualified && !isQualified ? { qualified_signature: true } : {})
        };
      })
      .filter(value => !!value);
  }

  parseLockSignerToCustomSignerInput(lockSigners: LockSignature[], isQualified: boolean = false): CustomSignerInput[] {
    return (lockSigners || []).map(signer => {
      const phoneVerification = signer.security_verifications?.find(v => v.type === SecurityVerificationEnum.Sms);
      const documentVerification = signer.security_verifications?.find(v => v.type !== SecurityVerificationEnum.Sms);
      const signerMainAttribute = signer.delivery_method as SignerMainAttributes;

      return {
        action: signer.action || ActionEnum.Sign,
        ...(signer.email ? { email: signer.email } : {}),
        ...(signer.name ? { name: signer.name } : {}),
        ...(signerMainAttribute === SignerMainAttributes.Sms && signer.phone ? { sms: signer.phone } : {}),
        ...(signerMainAttribute === SignerMainAttributes.Whatsapp && signer.phone ? { whatsapp: signer.phone } : {}),
        ...(signer.configs?.cnpj && (isQualified || signer.qualified_signature) ? { configs: { cnpj: signer.configs?.cnpj } } : {}),
        ...(signer.configs?.cpf ? { configs: { cpf: signer.configs?.cpf } } : {}),
        _extra: {
          isLocked: true,
          signerMainAttribute,
          showOptions: signer.show_options || false,
          showSmsVerification: signer.show_sms_verification || false,
          showCpfVerification: signer.show_cpf_verification || false,
          showSecurityVerifications: signer.show_security_verifications || false,
          fallbackEnabled: documentVerification?.fallback_behavior !== FallbackBehaviorEnum.DisableFallback,
          ...(phoneVerification?.verify_phone ? { phoneNumber: phoneVerification?.verify_phone } : {}),
          documentType: documentVerification?.type || SecurityVerificationEnum.Manual,
          isQualified: signer.qualified_signature || false
        },
        security_verifications: [] as SecurityVerificationInput[]
      };
    });
  }

  getActionButtonText(currentSigners: Signer, signerHasFieldsToFill: boolean): string {
    let text = '';
    if (signerHasFieldsToFill) {
      text += this.translateService.instant('signature.signAction.fill');
    }
    if (signerHasFieldsToFill && currentSigners?.action) {
      text += ' ' + this.translateService.instant('signature.and') + ' ';
    }
    if (currentSigners?.action) {
      const signAction = this.translateService.instant('signature.signAction.' + (currentSigners.action.name.toLowerCase() || 'sign'));
      if (signerHasFieldsToFill && currentSigners?.action) {
        text += signAction.toLowerCase();
      } else {
        text += signAction;
      }
    }
    return text;
  }

  removeDuplicateFields(originalFields: DynamicTemplateVariable[]) {
    type FieldDataForChecking = {
      name: string;
    };

    const checked: FieldDataForChecking[] = [];
    const result: DynamicTemplateVariable[] = [];

    originalFields.forEach((field: DynamicTemplateVariable) => {
      const fieldDataForChecking: FieldDataForChecking = {
        name: field.name
      };
      if (
        !checked.some((checkedField: FieldDataForChecking) => {
          return fieldDataForChecking.name == checkedField.name;
        })
      ) {
        result.push(field);
        checked.push(fieldDataForChecking);
      }
    });

    return result;
  }

  getAlreadyFilledFields(document: Document, variables: DynamicTemplateVariable[]) {
    let alreadyFilledFields: FieldsValuesBySigner[] = [];

    const alreadyFilledSigners: Signer[] = document.signatures.filter(signature => !!signature?.fields_filled && !!signature.fields_filled?.created_at);
    alreadyFilledFields = alreadyFilledFields.concat(this.getFieldsValuesBySigner(variables, false, alreadyFilledSigners));

    const removedSignersIndexes: number[] = Array.from(
      new Set(
        Object.values(variables || []).map(variable => {
          if (
            !document.signatures
              .map(signer => signer.dynamic_ids)
              .flat()
              .includes(variable.data_signer_id)
          ) {
            return variable.data_signer_id;
          }
        })
      )
    ).filter(index => !!index);
    alreadyFilledFields = alreadyFilledFields.concat(this.getFieldsValuesBySigner(variables, true, removedSignersIndexes));

    return alreadyFilledFields;
  }

  getFieldsValuesBySigner(variables: DynamicTemplateVariable[], isRemoved = false, signers: any[]) {
    return signers.map(signer => {
      const signerFields: DynamicTemplateVariable[] = this.removeDuplicateFields(
        Object.values(variables || []).filter(variable => (isRemoved ? variable.data_signer_id === signer : signer.dynamic_ids?.includes(variable.data_signer_id)))
      );

      let signerFieldsValues: Record<string, string> = {};
      signerFields.forEach((field: DynamicTemplateVariable, index: number) => {
        signerFieldsValues[field.name] = isRemoved ? '' : signer?.fields_filled.field_values[index];
      });

      return { signer: isRemoved ? signer : signerFields[0]?.data_signer_id, fields: signerFieldsValues };
    });
  }

  getFieldsDataToShowOnHistory(document: Document, templateVariables: DynamicTemplateVariable[]) {
    const signersData: Record<string, FieldDataToShow[]> = {};

    document.signatures.map((signer: any) => {
      const signerFields: DynamicTemplateVariable[] = this.removeDuplicateFields(Object.values(templateVariables || []).filter(variable => signer.dynamic_ids?.includes(variable.data_signer_id)));
      const signerFieldsValues: FieldDataToShow[] = [];

      signerFields.forEach((field: DynamicTemplateVariable, idx: number) => {
        signerFieldsValues.push({
          name: field.name,
          value: signer.fields_filled?.field_values[idx] || ''
        });
      });

      signersData[signer.public_id] = signerFieldsValues;
    });

    return signersData;
  }

  scaleRenderedDocumentToFit(target: HTMLElement, isMobile: boolean, options?: { widthFix?: number; margin?: number }): number {
    if (!target || isMobile) return 1;

    const availableWidth = document.body.clientWidth - (options.widthFix || 0);
    const currentWidth = target.scrollWidth + (options.margin || 0);

    if (availableWidth <= currentWidth) {
      return availableWidth / currentWidth;
    }
    return 1;
  }

  getLanguageEnumFromString(language: string): LanguageEnum {
    language = language.replace(/-|_/g, '').toLowerCase();

    switch (language) {
      case 'enus':
        return LanguageEnum.EnUs;
      case 'ptbr':
        return LanguageEnum.PtBr;
      case 'esmx':
        return LanguageEnum.EsMx;

      default:
        return LanguageEnum.EnUs;
    }
  }

  getLanguageOptionFromString(language: string): LanguageOptions {
    language = language.replace(/-|_/g, '').toLowerCase();

    switch (language) {
      case 'enus':
        return 'en-US';
      case 'ptbr':
        return 'pt-BR';
      case 'esmx':
        return 'es-MX';

      default:
        return 'en-US';
    }
  }

  navigateAfterAuth(redirectUrl: string, router: Router): void {
    if (/^https?:\/\//i.test(redirectUrl)) {
      let targetHost: string;
      try {
        targetHost = new URL(redirectUrl).host;
      } catch {
        return;
      }
      if (!this.isAutentiqueDomain(targetHost)) {
        return;
      }

      window.location.href = redirectUrl;
    } else {
      if (redirectUrl.includes('?')) {
        const [path, queryString] = redirectUrl.split('?');
        const queryParams = this.parseQueryString(queryString);
        router.navigate([path], { replaceUrl: true, queryParams });
      } else {
        router.navigate([redirectUrl], { replaceUrl: true });
      }
    }
  }

  get termsOfUseAddress(): string {
    return this.buildLocalizedUrl('termos-de-uso');
  }

  get privacyPolicyAddress(): string {
    return this.buildLocalizedUrl('politica-de-privacidade');
  }

  private get languagePrefix(): string {
    return this.translateService.currentLang === 'pt-BR' ? '/' : '/en/';
  }

  private buildLocalizedUrl(path: string): string {
    return `${this.baseUrl}${this.languagePrefix}${path}/`;
  }
}
